/**
 * Escolha do meio de pagamento da corrida.
 *
 * Pix, dinheiro e maquininha são pagamentos diretos ao motorista. Esta etapa
 * não coleta nem cadastra dados de cartão do passageiro.
 */
import React, { useEffect, useMemo, useState } from "react";
import { LuBanknote, LuCheck, LuCreditCard, LuQrCode, LuTriangleAlert, LuX } from "react-icons/lu";
import { ZunoVehicle } from "./ZunoVisuals";

const TERMINAL_FEE_RATE = 0.032;

function money(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

function parseMoney(value) {
  const text = String(value || "").trim();
  const normalized = text.includes(",") ? text.replace(/\./g, "").replace(",", ".") : text;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : null;
}

function categoryName(category) {
  if (category === "comfort" || category === "elite") return "Zuno Elite";
  if (category === "movel" || category === "go") return "Zuno Go";
  return "Zuno Moto";
}

export default function PaymentSelector({
  valorCorrida,
  onConfirmar,
  onVoltar,
  categoria = "moto",
  distanciaKm,
  duracaoMin,
}) {
  const fare = Number(valorCorrida || 0);
  const [method, setMethod] = useState(null);
  const [needsChange, setNeedsChange] = useState(false);
  const [changeFor, setChangeFor] = useState("");

  const categoryLabel = categoryName(categoria);
  const terminalFee = Number((fare * TERMINAL_FEE_RATE).toFixed(2));
  const terminalTotal = Number((fare + terminalFee).toFixed(2));
  const changeAmount = parseMoney(changeFor);
  const invalidChange = method === "dinheiro" && needsChange && changeFor !== ""
    && (changeAmount === null || changeAmount <= fare);
  const canConfirm = Boolean(method)
    && !(method === "dinheiro" && needsChange && (changeAmount === null || changeAmount <= fare));
  const finalAmount = method === "maquininha" ? terminalTotal : fare;

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onVoltar?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onVoltar]);

  const rideDetails = useMemo(() => {
    const parts = [];
    if (distanciaKm) parts.push(`${Number(distanciaKm).toFixed(1)} km`);
    if (duracaoMin) parts.push(`aprox. ${Math.round(duracaoMin)} min`);
    return parts.length ? parts.join(" · ") : "Distância e tempo calculados";
  }, [distanciaKm, duracaoMin]);

  const selectMethod = (nextMethod) => {
    setMethod(nextMethod);
    if (nextMethod !== "dinheiro") {
      setNeedsChange(false);
      setChangeFor("");
    }
  };

  const confirm = () => {
    if (!canConfirm) return;
    const cashGiven = method === "dinheiro" && needsChange ? changeAmount : null;
    onConfirmar({
      metodo: method,
      cardId: null,
      precisaTroco: method === "dinheiro" && needsChange,
      valorTroco: cashGiven,
      trocoValor: cashGiven === null ? null : Number((cashGiven - fare).toFixed(2)),
      taxaCartao: method === "maquininha" ? terminalFee : 0,
      valorFinal: finalAmount,
    });
  };

  const paymentCard = ({ id, icon, title, description, meta, children }) => {
    const selected = method === id;
    return (
      <div className="zuno-payment-method-wrap" key={id}>
        <button
          type="button"
          className="zuno-payment-method"
          data-active={selected ? "true" : "false"}
          data-tone={id}
          onClick={() => selectMethod(id)}
          aria-pressed={selected}
          aria-label={`${title}. ${description}${selected ? ". Selecionado" : ""}`}
        >
          <span className="zuno-payment-method-icon" aria-hidden="true">{icon}</span>
          <span className="zuno-payment-method-copy">
            <strong>{title}</strong>
            <small>{description}</small>
            {meta && <span className="zuno-payment-method-meta">{meta}</span>}
          </span>
          <span className="zuno-payment-check" aria-hidden="true">{selected && <LuCheck />}</span>
        </button>
        {selected && children}
      </div>
    );
  };

  return (
    <div className="payment-selector-overlay" role="dialog" aria-modal="true" aria-labelledby="payment-title">
      <section className="payment-selector-sheet">
        <div className="zuno-payment-handle" aria-hidden="true" />
        <header className="zuno-payment-header">
          <div>
            <h2 id="payment-title">Como deseja pagar?</h2>
            <p>Valor da corrida: <strong>{money(fare)}</strong></p>
          </div>
          <button type="button" className="zuno-payment-close" aria-label="Fechar formas de pagamento" onClick={onVoltar}>
            <LuX aria-hidden="true" />
          </button>
        </header>

        <div className="payment-summary" aria-label={`Resumo da corrida ${categoryLabel}`}>
          <ZunoVehicle type={categoria} />
          <div>
            <strong>{categoryLabel}</strong>
            <span>{rideDetails}</span>
          </div>
          <div><small>Valor estimado</small><strong>{money(fare)}</strong></div>
        </div>

        <div className="zuno-payment-methods" aria-label="Formas de pagamento">
          {paymentCard({
            id: "pix",
            icon: <LuQrCode />,
            title: "Pix",
            description: "Pague pelo seu banco",
          })}

          {paymentCard({
            id: "dinheiro",
            icon: <LuBanknote />,
            title: "Dinheiro",
            description: "Pague diretamente ao motorista",
            children: (
              <div className="zuno-payment-detail zuno-payment-detail--stack">
                <label className="zuno-payment-change-toggle">
                  <input
                    type="checkbox"
                    checked={needsChange}
                    onChange={(event) => {
                      setNeedsChange(event.target.checked);
                      setChangeFor("");
                    }}
                  />
                  <span aria-hidden="true"><LuCheck /></span>
                  Preciso de troco
                </label>
                {needsChange && (
                  <label className="zuno-payment-change-field">
                    Troco para
                    <span>R$ <input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      aria-invalid={invalidChange}
                      aria-describedby={invalidChange ? "change-error" : undefined}
                      value={changeFor}
                      onChange={(event) => setChangeFor(event.target.value.replace(/[^\d,.]/g, "").slice(0, 10))}
                      placeholder="0,00"
                    /></span>
                  </label>
                )}
                {invalidChange && (
                  <p className="zuno-payment-inline-error" id="change-error" role="alert">
                    <LuTriangleAlert aria-hidden="true" /> Informe um valor maior que {money(fare)}.
                  </p>
                )}
              </div>
            ),
          })}

          {paymentCard({
            id: "maquininha",
            icon: <LuCreditCard />,
            title: "Cartão na maquininha",
            description: "Pague diretamente ao motorista no final da corrida",
            meta: `Total presencial estimado: ${money(terminalTotal)}`,
          })}
        </div>

        <button type="button" className="zuno-payment-confirm" disabled={!canConfirm} onClick={confirm}>
          {method ? `Confirmar ${categoryLabel} · ${money(finalAmount)}` : "Escolha uma forma de pagamento"}
        </button>
      </section>
    </div>
  );
}
